import * as ReactTable from "@tanstack/react-table"
import * as React from "react"
import { ForwardedRef } from "react"
import AutoSizer from "react-virtualized-auto-sizer"
import { VariableSizeList } from "react-window"

import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IVariant } from "shared/hubrise/models"
import { useDeepCompareMemoise } from "shared/utils/react"

import Header from "./Header"
import Row from "./Row"
import type { PublicRowProps } from "./Row"
import { useColumns, useData } from "./hooks"
import { rowHeight, topMargin } from "./styles"
import { RowData } from "./types"
import { filterSorting } from "./utils"

type HeaderProps = {
  designatorsHeaders: Array<string>
  removeAllRefs: () => void
  openGenerateRefs: () => void
  variants: Array<IVariant>
  enableAll: (enabled: boolean, variantRef?: string) => void
  openBulkUpdatePrices?: (variant: IVariant | null) => void
  clearAllPrices?: (variant: IVariant) => void
}

type GridTableProps<ItemType> = HeaderProps &
  PublicRowProps<ItemType> & {
    items: Array<ItemType>
    selectedItem: ItemType | undefined
  }

const GridTable = <ItemType extends { id: number }>({
  designatorsHeaders,
  removeAllRefs,
  openGenerateRefs,
  variants,
  enableAll,
  openBulkUpdatePrices,
  clearAllPrices,
  items,
  selectedItem,
  ...rowProps
}: GridTableProps<ItemType>): JSX.Element => {
  const { remountKey } = useStatefulFieldRemount()

  // Moving this to the child Row component would cause a re-render of each Row on every page change:
  // useNavigateNoUpdate() returns the same function (unlike useNavigate), but still causes a re-render on every page
  // change as it depends on the location context.
  const navigateNoUpdate = useNavigateNoUpdate()

  // A new rowProps object is created on each render even if the props are the same, because it is a spread.
  const stableRowProps = useDeepCompareMemoise(rowProps)

  const { groupColumns, columns } = useColumns(
    stableRowProps,
    designatorsHeaders,
    removeAllRefs,
    openGenerateRefs,
    variants,
    enableAll,
    openBulkUpdatePrices,
    clearAllPrices,
    items,
  )
  const data = useData<ItemType>(items, stableRowProps, designatorsHeaders, variants)
  const tableRef = React.useRef<HTMLDivElement>(null)

  const [sorting, setSorting] = React.useState<ReactTable.SortingState>([])
  const filteredSorting = React.useMemo(() => filterSorting(sorting, groupColumns), [groupColumns, sorting])

  const table = ReactTable.useReactTable<RowData<ItemType>>({
    data,
    columns: groupColumns,
    state: {
      sorting: filteredSorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: ReactTable.getCoreRowModel(),
    getSortedRowModel: ReactTable.getSortedRowModel(),
    columnResizeMode: "onChange",
  })

  const firstHeaderRowVisible = variants.length > 0
  const header = (
    <Header
      headerGroups={firstHeaderRowVisible ? table.getHeaderGroups() : table.getHeaderGroups().slice(1)}
      tableRef={tableRef}
    />
  )
  const headerHeight = rowHeight * (firstHeaderRowVisible ? 2 : 1)

  const scrollToItem = React.useCallback(
    (list: VariableSizeList | null) => {
      if (selectedItem) {
        const indexOfSelectedItem = table
          .getRowModel()
          .rows.findIndex((row) => row.original.item.id === selectedItem?.id)
        list?.scrollToItem(indexOfSelectedItem - 1, "smart")
      }
    },
    [selectedItem, table],
  )

  return (
    <InnerListContext.Provider value={{ header }}>
      <AutoSizer>
        {({ height, width }) => (
          <VariableSizeList
            outerRef={tableRef}
            itemData={{ rows: table.getRowModel().rows, columns, selectedItem, navigateNoUpdate, rowProps }}
            itemSize={(index) =>
              index === 0
                ? topMargin + headerHeight // First item just makes room for the header
                : rowHeight
            }
            height={height ?? "100%"}
            width={width ?? "100%"}
            itemCount={table.getRowModel().rows.length + 1}
            innerElementType={InnerList}
            ref={scrollToItem}
            key={[
              remountKey,
              // Re-mount when the header height changes (2 rows with variants, 1 without)
              variants.length > 0 ? "_" : "",
              // Re-mount when sorting changes
              sorting.length > 0 ? `${sorting[0].id}_${sorting[0].desc ? "desc" : "asc"}` : "",
            ].join()}
          >
            {RowWrapper<ItemType>}
          </VariableSizeList>
        )}
      </AutoSizer>
    </InnerListContext.Provider>
  )
}

const InnerList = React.forwardRef(
  ({ children, ...rest }: { children: React.ReactNode; rest: any }, $ref: ForwardedRef<HTMLDivElement>) => (
    <InnerListContext.Consumer>
      {({ header }) => (
        <div ref={$ref} {...rest}>
          {header}
          {children}
        </div>
      )}
    </InnerListContext.Consumer>
  ),
)

const RowWrapper = <ItemType extends { id: number }>({
  data,
  index,
  style,
}: {
  data: {
    rows: Array<ReactTable.Row<RowData<ItemType>>>
    columns: Array<ReactTable.ColumnDef<RowData<ItemType>>>
    selectedItem: ItemType | undefined
    navigateNoUpdate: ReturnType<typeof useNavigateNoUpdate>
    rowProps: PublicRowProps<ItemType>
  }
  index: number
  style: React.CSSProperties
}): JSX.Element => {
  if (index === 0) {
    // Spacer for initial gap + header
    return <div />
  } else {
    const row = data.rows[index - 1]
    const item = row.original.item

    const isSelected = item.id === data.selectedItem?.id
    return (
      <Row
        index={index}
        key={item.id}
        item={item}
        row={row}
        columns={data.columns}
        isSelected={isSelected}
        navigateNoUpdate={data.navigateNoUpdate}
        style={style}
        {...data.rowProps}
      />
    )
  }
}

const InnerListContext = React.createContext<{ header: React.ReactNode }>({ header: null })

InnerList.displayName = "InnerList"

// We assume that the various callbacks are stable and don't need to be memoised by the parent component.
export default React.memo(GridTable, (prevProps, nextProps) => {
  return (
    prevProps.items === nextProps.items &&
    prevProps.selectedItem === nextProps.selectedItem &&
    prevProps.variants === nextProps.variants
  )
}) as typeof GridTable
